'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Image from 'next/image';

const navLinks = [
  { label: 'Home', href: '/' },
  {
    label: 'Treks',
    dropdown: [
      { label: 'Himalayan Treks', href: '/treks/himalayan' },
      { label: 'Weekend Treks', href: '/treks/weekend' },
      { label: 'Spiritual Treks', href: '/treks/spiritual' },
    ],
  },
  { label: 'About Us', href: '#about' },
  { label: 'Stories', href: '#' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const navRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);

  const lastScrollY = useRef(0);

  // Scroll handling (restore your original behavior)
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;

      setScrolled(currentY > 60);

      if (currentY < 80) {
        setVisible(true);
      } else if (currentY > lastScrollY.current + 8) {
        setVisible(false);
        setMenuOpen(false);
      } else if (currentY < lastScrollY.current - 8) {
        setVisible(true);
      }

      lastScrollY.current = currentY;

      
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navbar entrance animation
  useEffect(() => {
    const init = async () => {
      const { gsap } = await import('gsap');

      if (navRef.current) {
        gsap.fromTo(
          navRef.current,
          { y: -80, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.2, ease: 'power3.out', delay: 0.5 }
        );
      }
    };
    init();
  }, []);

  // Dropdown GSAP animation
  useEffect(() => {
    if (!dropdownRef.current) return;

    const init = async () => {
      const { gsap } = await import('gsap');

      if (activeDropdown) {
        gsap.fromTo(
          dropdownRef.current,
          { opacity: 0, y: -10, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.25,
            ease: 'power2.out',
          }
        );
      }
    };

    init();
  }, [activeDropdown]);

  return (
    <>
      <header data-component-id="src/components/Navbar.tsx:102:6" data-component-path="src/components/Navbar.tsx" data-component-line="102" data-component-end-line="192" data-component-file="Navbar.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          visible ? 'translate-y-0' : '-translate-y-full'
        } ${
          scrolled
            ? 'bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm'
            : 'bg-white'
        }`}
        style={{ opacity: 0 }}
      >
        

        <div data-component-id="src/components/Navbar.tsx:115:8" data-component-path="src/components/Navbar.tsx" data-component-line="115" data-component-end-line="191" data-component-file="Navbar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20h-16%20md%3Ah-20%20flex%20items-center%20justify-between%22%7D" className="max-w-7xl mx-auto px-6 h-16 md:h-20 flex items-center justify-between">
          {/* Logo */}
          <Link data-component-id="src/components/Navbar.tsx:117:10" data-component-path="src/components/Navbar.tsx" data-component-line="117" data-component-end-line="125" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22flex%20items-center%20gap-2.5%22%7D" href="/" className="flex items-center gap-2.5">
            <Image data-component-id="src/components/Navbar.tsx:118:12" data-component-path="src/components/Navbar.tsx" data-component-line="118" data-component-end-line="124" data-component-file="Navbar.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fassets%2Fimages%2Ftreks-and-trails-logo.png%22%2C%22alt%22%3A%22TreksTrails%20Logo%22%2C%22className%22%3A%22object-contain%22%7D"
  src="/assets/images/treks-and-trails-logo.png"
  alt="TreksTrails Logo"
  width={110}
  height={30}
  className="object-contain"
/>
          </Link>

          {/* Desktop Nav */}
          <nav data-component-id="src/components/Navbar.tsx:128:10" data-component-path="src/components/Navbar.tsx" data-component-line="128" data-component-end-line="172" data-component-file="Navbar.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20md%3Aflex%20items-center%20gap-8%20relative%22%7D" className="hidden md:flex items-center gap-8 relative">
            {navLinks.map((link) => {
              if (link.dropdown) {
                return (
                  <div data-component-id="src/components/Navbar.tsx:132:18" data-component-path="src/components/Navbar.tsx" data-component-line="132" data-component-end-line="158" data-component-file="Navbar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D"
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setActiveDropdown(link.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <button data-component-id="src/components/Navbar.tsx:138:20" data-component-path="src/components/Navbar.tsx" data-component-line="138" data-component-end-line="140" data-component-file="Navbar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-black%2F80%20hover%3Atext-black%20transition%22%7D" className="text-black/80 hover:text-black transition">
                      {link.label}
                    </button>

                    {activeDropdown === link.label && (
                      <div data-component-id="src/components/Navbar.tsx:143:22" data-component-path="src/components/Navbar.tsx" data-component-line="143" data-component-end-line="156" data-component-file="Navbar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20left-0%20mt-4%20bg-white%20shadow-xl%20rounded-xl%20py-4%20w-56%20border%22%7D"
                        ref={dropdownRef}
                        className="absolute top-full left-0 mt-4 bg-white shadow-xl rounded-xl py-4 w-56 border"
                      >
                        {link.dropdown.map((item) => (
                          <Link data-component-id="src/components/Navbar.tsx:148:26" data-component-path="src/components/Navbar.tsx" data-component-line="148" data-component-end-line="154" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22block%20px-5%20py-2%20text-sm%20text-black%2F70%20hover%3Atext-black%20hover%3Abg-gray-100%20transition%22%7D"
                            key={item.label}
                            href={item.href}
                            className="block px-5 py-2 text-sm text-black/70 hover:text-black hover:bg-gray-100 transition"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link data-component-id="src/components/Navbar.tsx:163:16" data-component-path="src/components/Navbar.tsx" data-component-line="163" data-component-end-line="169" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-black%2F80%20hover%3Atext-black%20transition%22%7D"
                  key={link.label}
                  href={link.href}
                  className="text-black/80 hover:text-black transition"
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* CTA */}
          <div data-component-id="src/components/Navbar.tsx:175:10" data-component-path="src/components/Navbar.tsx" data-component-line="175" data-component-end-line="182" data-component-file="Navbar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20md%3Aflex%22%7D" className="hidden md:flex">
            <Link data-component-id="src/components/Navbar.tsx:176:12" data-component-path="src/components/Navbar.tsx" data-component-line="176" data-component-end-line="181" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23featured-treks%22%2C%22className%22%3A%22bg-black%20text-white%20px-5%20py-2.5%20rounded-full%20text-sm%20font-semibold%20hover%3Abg-gray-800%20transition%22%2C%22textContent%22%3A%22Book%20a%20Trek%22%7D"
              href="#featured-treks"
              className="bg-black text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gray-800 transition"
            >
              Book a Trek
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button data-component-id="src/components/Navbar.tsx:185:10" data-component-path="src/components/Navbar.tsx" data-component-line="185" data-component-end-line="190" data-component-file="Navbar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22md%3Ahidden%20p-2%20text-black%22%7D"
            className="md:hidden p-2 text-black"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div data-component-id="src/components/Navbar.tsx:195:6" data-component-path="src/components/Navbar.tsx" data-component-line="195" data-component-end-line="234" data-component-file="Navbar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`fixed inset-0 z-40 bg-white flex flex-col items-center justify-center gap-8 transition-all duration-500 md:hidden ${
          menuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <nav data-component-id="src/components/Navbar.tsx:202:8" data-component-path="src/components/Navbar.tsx" data-component-line="202" data-component-end-line="233" data-component-file="Navbar.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20gap-6%20text-2xl%20font-bold%22%7D" className="flex flex-col items-center gap-6 text-2xl font-bold">
          <Link data-component-id="src/components/Navbar.tsx:203:10" data-component-path="src/components/Navbar.tsx" data-component-line="203" data-component-end-line="205" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22textContent%22%3A%22Home%22%7D" href="/" onClick={() => setMenuOpen(false)}>
            Home
          </Link>

          <div data-component-id="src/components/Navbar.tsx:207:10" data-component-path="src/components/Navbar.tsx" data-component-line="207" data-component-end-line="214" data-component-file="Navbar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%22%7D" className="flex flex-col items-center">
            <span data-component-id="src/components/Navbar.tsx:208:12" data-component-path="src/components/Navbar.tsx" data-component-line="208" data-component-end-line="208" data-component-file="Navbar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Treks%22%7D">Treks</span>
            <div data-component-id="src/components/Navbar.tsx:209:12" data-component-path="src/components/Navbar.tsx" data-component-line="209" data-component-end-line="213" data-component-file="Navbar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20flex%20flex-col%20gap-2%20text-lg%20font-medium%22%7D" className="mt-2 flex flex-col gap-2 text-lg font-medium">
              <Link data-component-id="src/components/Navbar.tsx:210:14" data-component-path="src/components/Navbar.tsx" data-component-line="210" data-component-end-line="210" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Ftreks%2Fhimalayan%22%2C%22textContent%22%3A%22Himalayan%20Treks%22%7D" href="/treks/himalayan">Himalayan Treks</Link>
              <Link data-component-id="src/components/Navbar.tsx:211:14" data-component-path="src/components/Navbar.tsx" data-component-line="211" data-component-end-line="211" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Ftreks%2Fweekend%22%2C%22textContent%22%3A%22Weekend%20Treks%22%7D" href="/treks/weekend">Weekend Treks</Link>
              <Link data-component-id="src/components/Navbar.tsx:212:14" data-component-path="src/components/Navbar.tsx" data-component-line="212" data-component-end-line="212" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Ftreks%2Fspiritual%22%2C%22textContent%22%3A%22Spiritual%20Treks%22%7D" href="/treks/spiritual">Spiritual Treks</Link>
            </div>
          </div>

          <Link data-component-id="src/components/Navbar.tsx:216:10" data-component-path="src/components/Navbar.tsx" data-component-line="216" data-component-end-line="218" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23about%22%2C%22textContent%22%3A%22About%20Us%22%7D" href="#about" onClick={() => setMenuOpen(false)}>
            About Us
          </Link>
          <Link data-component-id="src/components/Navbar.tsx:219:10" data-component-path="src/components/Navbar.tsx" data-component-line="219" data-component-end-line="221" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23%22%2C%22textContent%22%3A%22Stories%22%7D" href="#" onClick={() => setMenuOpen(false)}>
            Stories
          </Link>
          <Link data-component-id="src/components/Navbar.tsx:222:10" data-component-path="src/components/Navbar.tsx" data-component-line="222" data-component-end-line="224" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23contact%22%2C%22textContent%22%3A%22Contact%22%7D" href="#contact" onClick={() => setMenuOpen(false)}>
            Contact
          </Link>

          <Link data-component-id="src/components/Navbar.tsx:226:10" data-component-path="src/components/Navbar.tsx" data-component-line="226" data-component-end-line="232" data-component-file="Navbar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23featured-treks%22%2C%22className%22%3A%22mt-4%20bg-black%20text-white%20px-6%20py-3%20rounded-full%22%2C%22textContent%22%3A%22Book%20a%20Trek%22%7D"
            href="#featured-treks"
            className="mt-4 bg-black text-white px-6 py-3 rounded-full"
            onClick={() => setMenuOpen(false)}
          >
            Book a Trek
          </Link>
        </nav>
      </div>
    </>
  );
}